<template>
  <div>
    <div v-if="!consentGiven" :class="$style.consent">
      <h2>{{ consentHeading || fallbackConsentHeading }}</h2>
      <div v-html="vendorConsentText" />
      <Button1XP
        :class="$style.consentButton"
        size="s"
        :text="consentButtonLabel"
        @click="handleContentButton"
      />
    </div>
    <template v-if="consentGiven">
      <div :class="$style.container">
        <div ref="map" :class="$style.map" />
      </div>
    </template>
  </div>
</template>

<script setup>
//* prevent Eslint warnings
/*global google*/
import { onMounted, onUpdated, ref, computed, onBeforeMount } from 'vue';
import { getVendorConsent, registerConsentListeners } from '../../globals/consentmanager';
import cmpVendorIDs from '../../globals/constants/cmpVendorIDs';
import Button1XP from '../Button1XP/Button1XP.vue';
import { useStoreTranslations } from '../../store/translations/translations';

const props = defineProps({
  config: {
    type: Object,
    default: () => {},
  },
});

const storeTranslations = useStoreTranslations();
const dictionary = window.dictionary;

const vendor = cmpVendorIDs.GOOGLEMAPS;
const consentGiven = ref(window.Cookiebot ? true : getVendorConsent(vendor.id));

const consentHeading = computed(() => storeTranslations.translations?.['consentHeading']);

const vendorConsentText = computed(
  () =>
    storeTranslations.translations?.['consentText']
      ?.replaceAll('###VENDOR_NAME###', vendor.name)
      .replaceAll('#<VENDOR_NAME>#', vendor.name) || fallbackConsentText,
);

const consentButtonLabel = computed(
  () =>
    storeTranslations.translations?.['consentButtonLabel']?.replaceAll(/&apos;/g, "'") ||
    fallbackConsentButtonLabel,
);

const fallbackConsentHeading = computed(() => dictionary?.consentHeading);
const fallbackConsentText =
  dictionary?.consentText
    ?.replaceAll('###VENDOR_NAME###', vendor.name)
    .replaceAll('#<VENDOR_NAME>#', vendor.name) || fallbackConsentButtonLabel;
const fallbackConsentButtonLabel = dictionary?.consentButtonLabel?.replaceAll(/&apos;/g, "'");

const apiKey = 'AIzaSyCdM1m57HfyyDYNneMFZ_e8spr1PeSOpjM';
const address = [
  props.config.street,
  props.config.zip,
  props.config.city,
  props.config.country,
].join(',');
const infoWindow = ref(null);
const map = ref(null);
const mapOptions = ref({
  zoom: 15,
  disableDefaultUI: false,
  scaleControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  mapTypeControl: false,
  mapTypeId: null,
});

const callInit = () => {
  setTimeout(() => {
    if (typeof google === 'object' && typeof google.maps === 'object') {
      initGMaps();
    } else {
      callInit();
    }
  }, 200);
};

const checkVendorConsent = () => {
  consentGiven.value = getVendorConsent(vendor.id);
  checkConsent();
};

const handleContentButton = () => {
  if (window.cmpConsentType === 'consentmanager') {
    window.__cmp &&
      window.__cmp('setVendorConsent', [vendor.id, 1], () => {
        consentGiven.value = getVendorConsent(vendor.id);
        checkConsent();
      });
  } else {
    if (window.Cookiebot) {
      window.Cookiebot.onaccept = () => {
        consentGiven.value = true;
      };
    }
    checkConsent();
  }
};

const checkConsent = () => {
  if (consentGiven.value) {
    callInit();
  }
};

const initGMaps = () => {
  mapOptions.value.mapTypeId = google.maps.MapTypeId.HYBRID;
  const config = props.config;
  let directionsURL = '';
  let cta = '';

  if (config.directionsText !== '') {
    if (
      typeof config.latitude !== 'undefined' &&
      config.latitude !== '' &&
      typeof config.longitude !== 'undefined' &&
      config.longitude !== ''
    ) {
      directionsURL =
        'http://maps.google.com/?q=' +
        encodeURIComponent(config.latitude + ',' + config.longitude) +
        '&hl=' +
        config.language;
    } else {
      directionsURL =
        'http://maps.google.com/?daddr=' +
        encodeURIComponent(
          config.street + ',' + config.zip + ' ' + config.city + ',' + config.country,
        ) +
        '&hl=' +
        config.language;
    }

    cta = `
      <a
        class="Button1XP__container Button1XP__link Button1XP__link__type--linkStatic Button1XP__link__size--xs"
        href="${directionsURL}"
        target="_blank"
      >
        <span class="Button1XP__textButton">${config.directionsText}</span>
        <span class="material-symbols-outlined Icon__size-medium">arrow_forward</span>
      </a>
    `;
  }

  let contentString = `
    <div class="gContainer">
      <div class="gImage">
        <img src="${config.logo}" border="0" alt=""/>
      </div>
      <div class="gContent">
        <div class="company">${config.company}</div>
        ${config.addInfo !== '' ? `<div class="addInfo">${config.addInfo}</div>` : ''}
          <div class="address">
            ${config.street}<br />
            ${config.zip} ${config.city}
          </div>
        ${cta}
      </div>
    </div>`;

  infoWindow.value = new google.maps.InfoWindow({
    content: contentString,
    maxWidth: 320,
  });

  map.value = new google.maps.Map(map.value, mapOptions.value);

  const icon = {
    url: config.marker,
    scaledSize: new google.maps.Size(38, 38),
    origin: new google.maps.Point(0, 0),
    anchor: new google.maps.Point(0, 0),
  };

  if (
    typeof config.latitude !== 'undefined' &&
    config.latitude !== '' &&
    typeof config.longitude !== 'undefined' &&
    config.longitude !== ''
  ) {
    const newLatLng = new google.maps.LatLng(config.latitude, config.longitude);

    map.value.setCenter(newLatLng);

    const marker = new google.maps.Marker({
      position: newLatLng,
      map: map.value,
      icon,
      title: config.company,
      flat: true,
    });

    google.maps.event.addListener(marker, 'click', () => {
      infoWindow.value.open(map.value, marker);
    });

    infoWindow.value.open(map.value, marker);
  } else {
    const geocoder = new google.maps.Geocoder();
    const geoReq = {
      address: address,
      language: config.language,
      country: config.ccTLD,
    };

    geocoder.geocode(geoReq, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results.length) {
        if (status !== google.maps.GeocoderStatus.ZERO_RESULTS) {
          map.value.setCenter(results[0].geometry.location);

          const marker = new google.maps.Marker({
            position: results[0].geometry.location,
            map: map.value,
            icon,
            title: config.company,
            flat: true,
          });

          google.maps.event.addListener(marker, 'click', () => {
            infoWindow.value.open(map.value, marker);
          });

          infoWindow.value.open(map.value, marker);
        }
      }
    });
  }
};

const registerConsentListenersWithDelay = (checkVendorConsent) => {
  const intervalId = setInterval(() => {
    if (typeof window.__cmp === 'function') {
      registerConsentListeners(checkVendorConsent);
      clearInterval(intervalId);
    }
  }, 100);
  setTimeout(() => clearInterval(intervalId), 5000);
};

onBeforeMount(() => {
  if (window.cmpConsentType === 'consentmanager') {
    registerConsentListenersWithDelay(checkVendorConsent);
    setTimeout(() => {
      checkVendorConsent();
    }, 1000);
  } else {
    checkConsent();
  }
});

onMounted(() => {
  if (typeof google === 'undefined' || !window.googleApiInHead) {
    window.googleApiInHead = true;
    const script = document.createElement('script');
    if (props.config.language) {
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&language=${props.config.language}`;
    } else {
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    }
    script.setAttribute('async', '');
    script.setAttribute('defer', '');
    script.onload = callInit;
    document.head.appendChild(script);
  } else if (window.googleApiInHead) {
    callInit();
  }
});

onUpdated(() => {
  if (window.cmpConsentType === 'consentmanager') {
    registerConsentListeners(checkVendorConsent);
    checkVendorConsent();
  } else {
    checkConsent();
  }

  if (window.Cookiebot) {
    consentGiven.value = true;
  } else {
    consentGiven.value = getVendorConsent(vendor.id);
  }
});
</script>

<style module lang="scss">
@forward './GoogleMaps.scss';
</style>
